import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface AvailableVehicle {
  id: string;
  name: string;
  plate?: string;
  brand?: string;
  model?: string;
  type?: string;
}

export interface NetworkConfig {
  brand_name?: string;
  model_name?: string;
  ip_address: string;
  port: string;
  protocol: string;
}

export interface Device {
  /** Calibración del sensor de combustible (valores crudos de tanque vacío y lleno). */
  fuel_sensor?: { param: string; empty_raw: number; full_raw: number } | null;
  /** Botón de pánico: true sí, false no es botón, null/ausente sin revisar (utils/panicButton). */
  has_panic_button?: boolean | null;
  id: string;
  name?: string;
  vehicle_id: string;
  identifier: string;
  model: string;
  status: { id: string; name: string };
  status_id?: string;
  fuelLevel: number;
  batteryLevel: number;
  mileage: number;
  hoursMotor: number;
  vehicleName: string;
  type: string;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  lastLongitude?: number;
  lastLatitude?: number;
  collections: string[];
  company_id?: string;
  // Campos del backend
  serial?: string;
  sim_card?: string;
  iccid?: string;
  network?: NetworkConfig;
  phone_number?: string;
  installation_date?: string;
  activation_date?: string;
  brand_id?: string;
  vehicle?: {
    name: string;
    icon: string;
    plate: string;
    brand: string;
    model: string;
    year: number;
    type: string;
    fuelType: string;
    fuelCapacity: number;
    createdAt: { _seconds: number; _nanoseconds: number };
    status_id: string;
    updatedAt: { _seconds: number; _nanoseconds: number };
    company_id: string;
    id: string;
    collections: string[];
  } | null;
  remolque?: {
    name: string;
    icon: string;
    plate: string;
    brand: string;
    model: string;
    year: number;
    type: string;
  } | null;
}

export interface CreateDeviceData {
  name: string;
  vehicle_id?: string;
  identifier: string;
  model: string;
  status_id: string;
  fuelLevel?: number;
  batteryLevel?: number;
  mileage?: number;
  hoursMotor?: number;
  type: string;
  lastLatitude?: number;
  lastLongitude?: number;
  // Campos requeridos del backend
  serial: string;
  sim_card: string;
  iccid: string;
  network?: NetworkConfig;
  phone_number: string;
  installation_date: string;
  activation_date?: string;
  brand_id?: string;
}

export interface UpdateDeviceData {
  name?: string;
  vehicle_id?: string | null;
  identifier?: string;
  model?: string;
  status_id?: string;
  fuelLevel?: number;
  batteryLevel?: number;
  mileage?: number;
  hoursMotor?: number;
  type?: string;
  lastLatitude?: number;
  lastLongitude?: number;
  // Campos del backend
  serial?: string;
  sim_card?: string;
  iccid?: string;
  network?: NetworkConfig;
  phone_number?: string;
  installation_date?: string;
  activation_date?: string;
  brand_id?: string;
}

interface DevicesStore {
  devices: Device[];
  // Vehículos disponibles para asignar
  availableVehicles: AvailableVehicle[];
  availableVehiclesLoading: boolean;
  // Devices para sección admin (grupos, etc.) con scroll infinito
  adminSectionDevices: Device[];
  adminSectionCurrentPage: number;
  adminSectionHasMore: boolean;
  adminSectionLoading: boolean;
  adminSectionInitialLoading: boolean;
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchDevices: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    status?: string;
    type?: string;
    search?: string;
  }) => Promise<void>;
  // Fetch para sección admin con scroll infinito (grupos, etc.)
  fetchAdminSectionDevices: (params?: { limit?: number; reset?: boolean; companyId?: string }) => Promise<void>;
  resetAdminSectionDevices: () => void;
  // Fetch de vehículos disponibles (comentado para uso futuro)
  // fetchAvailableVehicles: () => Promise<void>;
  createDevice: (
    deviceData: Partial<CreateDeviceData>
  ) => Promise<{ success: boolean; data?: Device; error?: string }>;
  updateDevice: (
    deviceId: string,
    deviceData: UpdateDeviceData
  ) => Promise<{ success: boolean; data?: Device; error?: string }>;
  updateDeviceStatus: (
    deviceId: string,
    statusId: string
  ) => Promise<{ success: boolean; data?: Device; error?: string }>;
  deleteDevice: (
    deviceId: string
  ) => Promise<{ success: boolean; error?: string }>;
  // Métodos de conexión (attach/detach)
  attachVehicleToDevice: (
    deviceId: string,
    vehicleId: string
  ) => Promise<{ success: boolean; data?: any; error?: string }>;
  detachVehicleFromDevice: (
    deviceId: string
  ) => Promise<{ success: boolean; data?: any; error?: string }>;
  attachRemolqueToDevice: (
    deviceId: string,
    remolqueId: string
  ) => Promise<{ success: boolean; data?: any; error?: string }>;
  detachRemolqueFromDevice: (
    deviceId: string
  ) => Promise<{ success: boolean; data?: any; error?: string }>;
  // Fetch devices sin vehículo asignado
  fetchDevicesWithoutVehicle: () => Promise<Device[]>;
}

export const useDevicesStore = create<DevicesStore>((set, get) => ({
  devices: [],
  // Vehículos disponibles para asignar
  availableVehicles: [],
  availableVehiclesLoading: false,
  // Estado para sección admin (grupos, etc.) con scroll infinito
  adminSectionDevices: [],
  adminSectionCurrentPage: 1,
  adminSectionHasMore: true,
  adminSectionLoading: false,
  adminSectionInitialLoading: false,
  loading: false,
  error: null,
  meta: undefined,

  fetchDevices: async (params = {}) => {
    if (get().loading) return;
    const fetchGen = getAdminFetchGeneration();
    set({ loading: true, availableVehiclesLoading: true, error: null });

    try {
      // Hacer ambas peticiones en paralelo
      const [devicesResponse, vehiclesResponse] = await Promise.all([
        api.get("/deviceList", {
          params: {
            limit: params.limit ?? 8,
            page: params.page ?? 1,
            ...params,
          },
        }),
        api.get("/vehicleListWithoutDeviceSelected"),
      ]);

      if (getAdminFetchGeneration() !== fetchGen) {
        set({ loading: false, availableVehiclesLoading: false });
        return;
      }

      set({
        devices: devicesResponse.data.data || [],
        meta: devicesResponse.data.meta,
        availableVehicles: vehiclesResponse.data.data || [],
        loading: false,
        availableVehiclesLoading: false,
      });
    } catch (err: any) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(err)) {
        set({ loading: false, availableVehiclesLoading: false });
        return;
      }
      set({ error: getError(err), loading: false, availableVehiclesLoading: false });
    }
  },

  // Fetch para sección admin con scroll infinito (grupos, etc.)
  fetchAdminSectionDevices: async (params = {}) => {
    const { adminSectionLoading, adminSectionHasMore, adminSectionCurrentPage, adminSectionDevices } = get();

    if (adminSectionLoading || (!adminSectionHasMore && !params.reset)) return;

    const page = params.reset ? 1 : adminSectionCurrentPage;
    const limit = params.limit ?? 100;

    // Si es reset y no hay devices cargados, es carga inicial
    const isInitialLoad = params.reset && adminSectionDevices.length === 0;

    set({
      adminSectionLoading: true,
      adminSectionInitialLoading: isInitialLoad,
      error: null
    });

    try {
      const requestConfig: { params: Record<string, unknown>; headers?: Record<string, string> } = {
        params: { limit, page },
      };

      if (params.companyId) {
        requestConfig.headers = { "x-company-id": params.companyId };
        requestConfig.params.company_id = params.companyId;
      }

      const { data } = await api.get("/deviceList", requestConfig);

      const newDevices = Array.isArray(data.data) ? data.data : [];
      const totalPages = data.meta?.totalPages || 1;
      const hasMore = page < totalPages;

      const currentDevices = Array.isArray(adminSectionDevices) ? adminSectionDevices : [];

      set({
        adminSectionDevices: params.reset ? newDevices : [...currentDevices, ...newDevices],
        adminSectionCurrentPage: page + 1,
        adminSectionHasMore: hasMore,
        adminSectionLoading: false,
        adminSectionInitialLoading: false,
      });
    } catch (err: any) {
      set({ error: getError(err), adminSectionLoading: false, adminSectionInitialLoading: false });
    }
  },

  resetAdminSectionDevices: () => {
    set({
      adminSectionDevices: [],
      adminSectionCurrentPage: 1,
      adminSectionHasMore: true,
      adminSectionLoading: false,
      adminSectionInitialLoading: false,
    });
  },

  // Función para actualizar vehículos disponibles (comentada para uso futuro)
  // fetchAvailableVehicles: async () => {
  //   if (get().availableVehiclesLoading) return;
  //   set({ availableVehiclesLoading: true });
  //
  //   try {
  //     const { data } = await api.get("/vehicleListWithoutDeviceSelected");
  //     set({
  //       availableVehicles: data.data || [],
  //       availableVehiclesLoading: false,
  //     });
  //   } catch (err: any) {
  //     set({ availableVehiclesLoading: false });
  //   }
  // },

  createDevice: async (deviceData: Partial<CreateDeviceData>) => {
    try {
      const { data } = await api.post("/device", deviceData);

      const newDevice = data.data;
      set((state) => ({
        devices: [...state.devices, newDevice],
      }));

      return { success: true, data: newDevice };
    } catch (err: any) {
      console.error("Error creating device:", err);
      return { success: false, error: getError(err) };
    }
  },

  updateDevice: async (deviceId: string, deviceData: UpdateDeviceData) => {
    try {
      const { data } = await api.put("/device", deviceData, {
        params: { id: deviceId },
      });

      const updatedDevice = data.data;
      set((state) => ({
        devices: state.devices.map((device) =>
          device.id === deviceId ? updatedDevice : device
        ),
      }));

      return { success: true, data: updatedDevice };
    } catch (err: any) {
      console.error("Error updating device:", err);
      return { success: false, error: getError(err) };
    }
  },

  updateDeviceStatus: async (deviceId: string, statusId: string) => {
    try {
      const { data } = await api.put(
        "/deviceStatus",
        { status_id: statusId },
        { params: { id: deviceId } },
      );

      const updatedDevice = data.data;
      set((state) => ({
        devices: state.devices.map((device) =>
          device.id === deviceId ? { ...device, ...updatedDevice } : device
        ),
      }));

      return { success: true, data: updatedDevice };
    } catch (err: any) {
      console.error("Error updating device status:", err);
      return { success: false, error: getError(err) };
    }
  },

  deleteDevice: async (deviceId: string) => {
    try {
      await api.delete(`/device?id=${deviceId}`);

      set((state) => ({
        devices: state.devices.filter((device) => device.id !== deviceId),
      }));

      return { success: true };
    } catch (err: any) {
      console.error("Error deleting device:", err);
      return { success: false, error: getError(err) };
    }
  },

  // ============================================================================
  // MÉTODOS DE CONEXIÓN (ATTACH/DETACH)
  // ============================================================================

  attachVehicleToDevice: async (deviceId: string, vehicleId: string) => {
    try {
      const { data } = await api.post("/attachVehicle", {
        device_id: deviceId,
        vehicle_id: vehicleId,
      });

      // Actualizar el device en el store local
      set((state) => ({
        devices: state.devices.map((device) =>
          device.id === deviceId ? { ...device, vehicle_id: vehicleId } : device
        ),
      }));

      return { success: true, data: data.data };
    } catch (err: any) {
      console.error("Error attaching vehicle to device:", err);
      return { success: false, error: getError(err) };
    }
  },

  detachVehicleFromDevice: async (deviceId: string) => {
    try {
      const { data } = await api.post("/detachVehicle", {
        device_id: deviceId,
      });

      // Actualizar el device en el store local
      set((state) => ({
        devices: state.devices.map((device) =>
          device.id === deviceId ? { ...device, vehicle_id: "", vehicle: null } : device
        ),
      }));

      return { success: true, data: data.data };
    } catch (err: any) {
      console.error("Error detaching vehicle from device:", err);
      return { success: false, error: getError(err) };
    }
  },

  attachRemolqueToDevice: async (deviceId: string, remolqueId: string) => {
    try {
      const { data } = await api.post("/attachRemolque", {
        device_id: deviceId,
        remolque_id: remolqueId,
      });

      return { success: true, data: data.data };
    } catch (err: any) {
      console.error("Error attaching remolque to device:", err);
      return { success: false, error: getError(err) };
    }
  },

  detachRemolqueFromDevice: async (deviceId: string) => {
    try {
      const { data } = await api.post("/detachRemolque", {
        device_id: deviceId,
      });

      return { success: true, data: data.data };
    } catch (err: any) {
      console.error("Error detaching remolque from device:", err);
      return { success: false, error: getError(err) };
    }
  },

  fetchDevicesWithoutVehicle: async () => {
    try {
      const { data } = await api.get("/deviceListWithoutVehicle");
      return data.data || [];
    } catch (err: any) {
      console.error("Error fetching devices without vehicle:", err);
      return [];
    }
  },
}));
